import React, { useState, Children } from 'react';
import { BookIcon, FileTextIcon, SendIcon } from 'lucide-react';
import { ConnectionPageLayout } from '../../components/connections/ConnectionPageLayout';
import { FilterSection } from '../../components/UI/FilterSidebar';
import { OpportunityItem } from '../../components/connections/OpportunityPreviewCard';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
const ConnectWithProduction = () => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    projectType: '',
    serviceType: '',
    bookDetails: '',
    timeline: '',
    budget: '',
    additionalInfo: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const inputClass = `w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${rt.inputFocus} ${dk.inputOnCard}`;
  const sectionHeadingClass = `text-xl font-semibold mb-4 flex items-center ${dk.textPrimary}`;
  const labelClass = `block text-sm font-medium mb-1 ${dk.textSecondary}`;
  // Filters
  const filterSections: FilterSection[] = [{
    id: 'sort',
    title: 'Sort By',
    type: 'radio',
    options: [{
      value: 'relevance',
      label: 'Relevance'
    }, {
      value: 'rating-desc',
      label: 'Highest Rated'
    }],
    value: 'relevance',
    onChange: () => {}
  }, {
    id: 'availability',
    title: 'Availability',
    type: 'checkbox',
    options: [{
      value: 'open',
      label: 'Available Now'
    }, {
      value: 'booking',
      label: 'Booking Future'
    }],
    value: [],
    onChange: () => {}
  }, {
    id: 'serviceType',
    title: 'Service Type',
    type: 'checkbox',
    options: [{
      value: 'formatting',
      label: 'Formatting'
    }, {
      value: 'cover',
      label: 'Cover Design'
    }, {
      value: 'interior',
      label: 'Interior Layout'
    }, {
      value: 'ebook',
      label: 'eBook Conversion'
    }],
    value: [],
    onChange: () => {}
  }];
  // Opportunities
  const opportunities: OpportunityItem[] = [{
    id: '1',
    title: 'Interior Formatting for Fantasy Novel',
    poster: 'Marcus Chen',
    deadline: '2023-11-15',
    type: 'Fixed Price',
    description: 'Need professional formatting for a 400-page fantasy novel. Includes chapter headers and map placement.'
  }, {
    id: '2',
    title: 'Cover Design for Thriller',
    poster: 'Elena Richards',
    deadline: '2023-12-10',
    type: 'Fixed Price',
    description: 'Looking for a cover designer for a psychological thriller. Dark, moody aesthetic required.'
  }];
  const formContent = <form onSubmit={handleSubmit} className="space-y-6" data-id="element-13854" data-mp="8fch6xddq1dh">
      {/* Project Information */}
      <div className={`shadow-md rounded-lg p-6 border ${dk.card}`} data-id="element-13855" data-mp="ciqydrd20nsi">
        <h2 className={sectionHeadingClass} data-id="element-13856" data-mp="hh29e5f354cb">
          <BookIcon className={`h-5 w-5 mr-2 ${rt.textPrimary}`} data-id="element-13857" data-mp="hfr4u3dtfsk5" />
          Project Information
        </h2>
        <div className="grid grid-cols-1 gap-6" data-id="element-13858" data-mp="r7x3tx2hja8u">
          <div data-id="element-13859" data-mp="r6ls9b2ssy76">
            <label htmlFor="projectType" className={labelClass} data-id="element-13860" data-mp="r757gk12d8hi">
              Project Type
            </label>
            <select id="projectType" name="projectType" value={formData.projectType} onChange={handleChange} className={inputClass} required data-id="element-13861" data-mp="w4x2hmx09apx">
              <option value="" data-id="element-13862" data-mp="1sywn7b3kbx06">Select project type</option>
              <option value="fiction" data-id="element-13863" data-mp="1swagfdohxlj0">Fiction Book</option>
              <option value="nonfiction" data-id="element-13864" data-mp="26oqhwa1r1bht">Non-Fiction Book</option>
              <option value="academic" data-id="element-13865" data-mp="22ogb4v64uh1w">Academic/Technical</option>
              <option value="childrens" data-id="element-13866" data-mp="22qavo8dhs75g">Children's Book</option>
              <option value="poetry" data-id="element-13867" data-mp="1ezn8ygyx3cyu">Poetry Collection</option>
              <option value="other" data-id="element-13868" data-mp="19zsiufmyureu">Other</option>
            </select>
          </div>
          <div data-id="element-13869" data-mp="1a1vjrupnq4yc">
            <label htmlFor="serviceType" className={labelClass} data-id="element-13870" data-mp="1nz21dlyn9xlz">
              Service Type
            </label>
            <select id="serviceType" name="serviceType" value={formData.serviceType} onChange={handleChange} className={inputClass} required data-id="element-13871" data-mp="1izpamq4nk6e4">
              <option value="" data-id="element-13872" data-mp="2ueulihextb8t">Select service type</option>
              <option value="typesetting" data-id="element-13873" data-mp="32vv2j3eb5qn3">Professional Typesetting</option>
              <option value="formatting" data-id="element-13874" data-mp="36xzxq6kemvbu">Book Formatting</option>
              <option value="layout" data-id="element-13875" data-mp="36zb0c3245x1d">Interior Layout Design</option>
              <option value="ebook" data-id="element-13876" data-mp="2sz8lkpp3rjqc">eBook Conversion</option>
              <option value="print" data-id="element-13877" data-mp="2xxa4namj3j1t">Print Book Design</option>
              <option value="illustrated" data-id="element-13878" data-mp="2xxtl0mwj5zdq">Illustrated Book Design</option>
            </select>
          </div>
          <div data-id="element-13879" data-mp="3lnnwp1545hwe">
            <label htmlFor="bookDetails" className={labelClass} data-id="element-13880" data-mp="3lp8hhz1mlmh2">
              Book Details
            </label>
            <textarea id="bookDetails" name="bookDetails" value={formData.bookDetails} onChange={handleChange} rows={4} className={inputClass} placeholder="Word count, special formatting needs, illustrations, etc." required data-id="element-13881" data-mp="3bx2pwqfad72e" />
          </div>
        </div>
      </div>

      {/* Timeline and Budget */}
      <div className={`shadow-md rounded-lg p-6 border ${dk.card}`} data-id="element-13882" data-mp="n3b5vqzskw81">
        <h2 className={sectionHeadingClass} data-id="element-13883" data-mp="i3yj168x7p5g">
          <FileTextIcon className={`h-5 w-5 mr-2 ${rt.textPrimary}`} data-id="element-13884" data-mp="i4zsx7e7a5ll" />
          Timeline & Budget
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-13885" data-mp="d3ahvrqz9rxi">
          <div data-id="element-13886" data-mp="93tcszfgqwsv">
            <label htmlFor="timeline" className={labelClass} data-id="element-13887" data-mp="928inax29fvj">
              Desired Timeline
            </label>
            <select id="timeline" name="timeline" value={formData.timeline} onChange={handleChange} className={inputClass} required data-id="element-13888" data-mp="44ysft1ul06n">
              <option value="" data-id="element-13889" data-mp="3qhf9ekncbjyd">Select timeline</option>
              <option value="urgent" data-id="element-13890" data-mp="3llq5k54afgw9">Urgent (1-2 weeks)</option>
              <option value="standard" data-id="element-13891" data-mp="3hitv51owir3e">Standard (2-4 weeks)</option>
              <option value="flexible" data-id="element-13892" data-mp="3hl6973rjghb6">Flexible (4+ weeks)</option>
            </select>
          </div>
          <div data-id="element-13893" data-mp="3cm3dwb1d1pge">
            <label htmlFor="budget" className={labelClass} data-id="element-13894" data-mp="1s1ybd8k8sfcq">
              Budget Range (per page)
            </label>
            <select id="budget" name="budget" value={formData.budget} onChange={handleChange} className={inputClass} required data-id="element-13895" data-mp="1x0jbl7zhuz16">
              <option value="" data-id="element-13896" data-mp="260o82n35gd0k">Select budget range</option>
              <option value="economy" data-id="element-13897" data-mp="19bgnbhacu7xp">Economy ($2-3 per page)</option>
              <option value="standard" data-id="element-13898" data-mp="19db3yjelfdvz">Standard ($3-4 per page)</option>
              <option value="premium" data-id="element-13899" data-mp="1ddv3tuqjwvlt">Premium ($4+ per page)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div className={`shadow-md rounded-lg p-6 border ${dk.card}`} data-id="element-13900" data-mp="1n2leduznprwf">
        <h2 className={sectionHeadingClass} data-id="element-13901" data-mp="1n4y4wwjhdal3">
          <SendIcon className={`h-5 w-5 mr-2 ${rt.textPrimary}`} data-id="element-13902" data-mp="po95talx0mfe" />
          Contact Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-13903" data-mp="ulircf66odxy">
          <div data-id="element-13904" data-mp="3k4m7m5v6fksg">
            <label htmlFor="name" className={labelClass} data-id="element-13905" data-mp="3k446autnqgdo">
              Your Name
            </label>
            <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} className={inputClass} required data-id="element-13906" data-mp="3f6cf630u6yaj" />
          </div>
          <div data-id="element-13907" data-mp="3p5tgk8922q0p">
            <label htmlFor="email" className={labelClass} data-id="element-13908" data-mp="6y42cjgjvqfy">
              Email Address
            </label>
            <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} className={inputClass} required data-id="element-13909" data-mp="1zamikdsjncm" />
          </div>
        </div>
        <div className="mt-6" data-id="element-13910" data-mp="br6my23cnpk0">
          <label htmlFor="additionalInfo" className={labelClass} data-id="element-13911" data-mp="po2bz3zzxrlp">
            Additional Information
          </label>
          <textarea id="additionalInfo" name="additionalInfo" value={formData.additionalInfo} onChange={handleChange} rows={4} className={inputClass} placeholder="Any other details you'd like to share..." data-id="element-13912" data-mp="kqjx6wzasi5s" />
        </div>
      </div>

      {/* Submit */}
      <div className="flex justify-end" data-id="element-13913" data-mp="vcyqpm5rc4xb">
        <button type="submit" className={`inline-flex items-center px-6 py-3 rounded-md shadow-sm text-base font-medium text-white ${rt.primaryButton}`} data-id="element-13914" data-mp="zbfz090wzlgb">
          Submit Request
        </button>
      </div>
    </form>;
  return <ConnectionPageLayout title="Connect with Production" subtitle="Get your book ready for publication with expert interior design and formatting" icon={<BookIcon className="h-8 w-8" data-id="element-13916" data-mp="gm9bm8p6e5o6" />} vendorType="production" filterSections={filterSections} opportunities={opportunities} opportunitiesLink="/services/production/opportunities" formContent={formContent} formCTALabel="Submit a Production Request" formCTADescription="Need help formatting your book? Submit your project details and we'll find a pro for you." data-id="element-13915" data-mp="qdcgzmp516t9" />;
};
export default ConnectWithProduction;